import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";
import { User } from "./@types";
import RolesDropdown from "./ui/RoleDropDown";
import { useCreateUserMutation, useUpdateUserMutation } from "./userApiSlice";

export const userFormSchema = z.object({
  name: z.string().nonempty("Name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().nonempty("Phone number is required"),
  role: z.string().nonempty("Role is required"),
  address: z.string().nonempty("Address is required"),
});

export type UserFormData = z.infer<typeof userFormSchema>;

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  mode?: "add" | "edit";
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  user = null,
  mode = user ? "edit" : "add",
}) => {
  const { user: currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && user;

  const [
    createUser,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreateUserMutation();

  const [
    updateUser,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateUserMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      role: "",
      address: "",
    },
  });

  // Populate form with user data when editing
  useEffect(() => {
    if (isEditMode && user) {
      reset({
        name: user.fullName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        role: user.userType || "",
        address: user.address || "",
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        name: "",
        email: "",
        phoneNumber: "",
        role: "",
        address: "",
      });
    }
  }, [isEditMode, user, reset]);

  const onSubmit = async (data: UserFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        fullName: data.name,
        email: data.email,
        role: data.role,
        phoneNumber: data.phoneNumber,
        address: data.address,
        isProStaff: true, // Always true for users
      };

      if (isEditMode && user?.id) {
        await updateUser({
          id: user.id,
          ...payload,
        }).unwrap();
      } else {
        await createUser(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} user. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `User ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} user. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`User has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : `${isEditMode ? "Edit" : "Add"} user`;
  const submitButtonText = isEditMode ? "Update user" : "+ Add user";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-8"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <AppInputField
                  label="Enter name"
                  type="text"
                  error={errors.name?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter name"
                />
              )}
            />

            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <AppInputField
                  label="Enter email"
                  type="email"
                  error={errors.email?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter email"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <AppInputField
                  label="Phone number"
                  type="tel"
                  error={errors.phoneNumber?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter phone number"
                />
              )}
            />

            <RolesDropdown
              value={watch("role")}
              onChange={(value) =>
                setValue("role", value, { shouldValidate: true })
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              uiError={errors.role?.message}
            />
          </div>

          <div>
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <AppInputField
                  label="Address"
                  type="text"
                  error={errors.address?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter address"
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default UserModal;

//  || (isEditMode && !isDirty)
